import { Head, router } from '@inertiajs/react';
import AppLayout from '@/Layouts/AppLayout';
import Card, { CardBody } from '@/Components/Card';
import Button from '@/Components/Button';
import Badge from '@/Components/Badge';
import PageHeader from '@/Components/PageHeader';
import Alert from '@/Components/Alert';
import { ArrowLeftIcon, CheckIcon, XMarkIcon, ArrowDownTrayIcon, DocumentTextIcon, ShieldCheckIcon, BuildingOfficeIcon, PaperClipIcon } from '@heroicons/react/24/outline';

function InfoRow({ label, value }) {
    return value ? (
        <div className="py-3 border-b border-slate-100 last:border-0 flex flex-col sm:flex-row sm:justify-between gap-1">
            <span className="text-sm text-slate-500">{label}</span>
            <span className="text-sm font-medium text-slate-800">{value}</span>
        </div>
    ) : null;
}

const docTypeLabels = {
    vat_certificate: 'VAT / TRN Certificate',
    trade_license: 'Trade License',
    other: 'Attachment',
};

const docTypeColors = {
    vat_certificate: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
    trade_license: { bg: 'bg-blue-100', text: 'text-blue-600' },
    other: { bg: 'bg-purple-100', text: 'text-purple-600' },
};

export default function Show({ supplier, flash }) {
    const statusBadge = (s) => {
        const map = { approved: 'success', pending: 'warning', rejected: 'danger' };
        return <Badge variant={map[s] || 'neutral'}>{s}</Badge>;
    };

    const primaryContact = supplier.contacts?.find(c => c.is_primary) || supplier.contacts?.[0];

    return (
        <AppLayout>
            <Head title={`Supplier: ${supplier.company_name}`} />
            <PageHeader title={supplier.company_name} subtitle="Supplier Details" actions={
                <div className="flex gap-2 flex-wrap">
                    <Button href="/suppliers" variant="outline" size="sm"><ArrowLeftIcon className="w-4 h-4" /> Back</Button>
                    {supplier.status === 'pending' && (
                        <>
                            <Button size="sm" onClick={() => router.patch(`/suppliers/${supplier.id}/status`, { status: 'approved' })}><CheckIcon className="w-4 h-4" /> Approve</Button>
                            <Button variant="danger" size="sm" onClick={() => router.patch(`/suppliers/${supplier.id}/status`, { status: 'rejected' })}><XMarkIcon className="w-4 h-4" /> Reject</Button>
                        </>
                    )}
                </div>
            } />
            {flash?.success && <Alert type="success" className="mb-4">{flash.success}</Alert>}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Company Info */}
                <Card>
                    <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center">
                        <h3 className="font-semibold text-slate-800">Company Information</h3>
                        {statusBadge(supplier.status)}
                    </div>
                    <CardBody>
                        <InfoRow label="Company Name" value={supplier.company_name} />
                        <InfoRow label="Country" value={supplier.country_name} />
                        <InfoRow label="City" value={supplier.city} />
                        <InfoRow label="Website" value={supplier.website ? (
                            <a href={supplier.website} target="_blank" rel="noopener" className="text-emerald-700 hover:underline">{supplier.website}</a>
                        ) : null} />
                        <InfoRow label="VAT / TRN Number" value={supplier.vat_number} />
                        <InfoRow label="Registered" value={new Date(supplier.created_at).toLocaleDateString()} />
                    </CardBody>
                </Card>

                {/* Contacts */}
                <Card>
                    <div className="px-6 py-4 border-b border-slate-100">
                        <h3 className="font-semibold text-slate-800">Contact Persons</h3>
                    </div>
                    <CardBody>
                        {supplier.contacts && supplier.contacts.length > 0 ? (
                            <div className="space-y-4">
                                {supplier.contacts.map((contact, i) => (
                                    <div key={i} className={`p-4 rounded-xl border ${contact.is_primary ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200 bg-slate-50/50'}`}>
                                        {contact.is_primary && (
                                            <span className="inline-block text-[10px] font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full mb-2 uppercase">Primary</span>
                                        )}
                                        <InfoRow label="Name" value={contact.contact_person} />
                                        <InfoRow label="Email" value={contact.email} />
                                        <InfoRow label="Phone" value={contact.phone_code && contact.telephone ? `${contact.phone_code} ${contact.telephone}` : contact.telephone} />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-slate-400">No contacts available</p>
                        )}
                    </CardBody>
                </Card>
            </div>

            {/* Documents */}
            {supplier.documents && supplier.documents.length > 0 && (
                <Card className="mt-6">
                    <div className="px-6 py-4 border-b border-slate-100">
                        <h3 className="font-semibold text-slate-800">Documents ({supplier.documents.length})</h3>
                    </div>
                    <CardBody>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {supplier.documents.map((doc, i) => {
                                const docType = doc.document_type || 'other';
                                const colors = docTypeColors[docType] || docTypeColors.other;
                                const label = docTypeLabels[docType] || 'Document';

                                return (
                                    <a key={i}
                                       href={doc.download_url}
                                       target="_blank"
                                       rel="noopener"
                                       className="flex items-center gap-3 p-4 rounded-xl border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/50 transition-colors group"
                                    >
                                        <div className={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                                            {docType === 'vat_certificate' ? (
                                                <ShieldCheckIcon className={`w-5 h-5 ${colors.text}`} />
                                            ) : docType === 'trade_license' ? (
                                                <BuildingOfficeIcon className={`w-5 h-5 ${colors.text}`} />
                                            ) : (
                                                <PaperClipIcon className={`w-5 h-5 ${colors.text}`} />
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-slate-800 truncate">{label}</p>
                                            <p className="text-xs text-slate-400 truncate">{doc.original_name}</p>
                                            <p className="text-[11px] text-slate-300">{doc.human_file_size} &middot; {doc.extension?.toUpperCase()}</p>
                                        </div>
                                        <ArrowDownTrayIcon className="w-4 h-4 text-slate-300 group-hover:text-emerald-600 flex-shrink-0" />
                                    </a>
                                );
                            })}
                        </div>
                    </CardBody>
                </Card>
            )}
        </AppLayout>
    );
}
